import React, { useState } from 'react';
import { Tag, Modal } from 'antd';
import UserAvatar from '../common/UserAvatar';
import { LikeOutlined, CloseOutlined, UndoOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { MEMBER_ROLE_OPTIONS, CAREER_OPTIONS, CAREER_COLOR_MAP } from '../../utils/constants';

/**
 * 成员卡片组件
 * @param {Object} props - 组件属性
 * @param {Object} props.member - 成员数据
 * @param {string} props.member.id - 成员ID
 * @param {string} props.member.user_id - 成员用户ID（用于获取用户详情）
 * @param {string} props.member.name - 成员名称
 * @param {string} props.member.avatar - 成员头像URL
 * @param {string} props.member.role - 成员身份/角色
 * @param {number} props.member.like_count - 获赞数
 * @param {string} props.member.member_status - 成员状态（confirmed/invited）
 * @param {string} props.member.invitation_id - 邀请ID（邀请成员时存在）
 * @param {Function} props.onDelete - 删除成员回调（可选）
 * @param {Function} props.onRevokeInvitation - 撤回邀请回调（可选）
 * @param {boolean} props.showDelete - 是否显示删除按钮
 * @returns {ReactElement} 成员卡片组件
 */
const MemberCard = ({ 
  member, 
  onDelete, 
  onRevokeInvitation,
  showDelete = false,
}) => {

  const navigate = useNavigate();
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [revokeModalVisible, setRevokeModalVisible] = useState(false);

  // 根据预期职业获取标签颜色和显示文本
  const getCareerInfo = () => {
    // 如果是队长，直接显示队长标签
    if (member.role === 'captain') {
      return { 
        label: '队长', 
        color: 'bg-red-500 text-white' 
      };
    }

    // 如果是邀请成员，显示特殊的标签样式
    if (member.member_status === 'invited') {

      // 从成员数据中获取预期职业（新数据结构中在 preferences.expected_career 中）
      const expectedCareer = member.preferences?.expected_career;
      
      if (expectedCareer) {
        // 根据预期职业设置不同的颜色
        const careerColor = CAREER_COLOR_MAP[expectedCareer] || 'bg-gray-500 text-white';
        
        return { 
          label: `邀请中 (${expectedCareer})`, 
          color: `${careerColor} border-2 border-orange-300` 
        };
      } else {
        // 如果没有预期职业信息，使用角色信息作为备选
        const roleOption = MEMBER_ROLE_OPTIONS.find(option => option.value === member.role);
        const roleLabel = roleOption ? roleOption.label : '成员';
        return { 
          label: `邀请中 (${roleLabel})`, 
          color: 'bg-orange-500 text-white border-2 border-orange-300' 
        };
      }
    }

    // 从成员数据中获取预期职业（新数据结构中在 preferences.expected_career 中）
    const expectedCareer = member.preferences?.expected_career;

    // 如果没有预期职业信息，显示默认标签
    if (!expectedCareer) {
      return { label: '未设置', color: 'bg-gray-500 text-white' };
    }

    // 根据预期职业设置不同的颜色
    const careerColor = CAREER_COLOR_MAP[expectedCareer] || 'bg-gray-500 text-white';
    
    return { label: expectedCareer, color: careerColor };
  };

  // 格式化获赞数显示
  const formatLikeCount = (count) => {
    if (!count || count === 0) return '0';
    if (count >= 10000) {
      return `${(count / 10000).toFixed(1)}W`;
    }
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}k`;
    }
    return count.toString();
  };

  // 处理删除按钮点击
  const handleDeleteClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setDeleteModalVisible(true);
  };

  // 确认删除
  const handleConfirmDelete = () => {
    if (onDelete) {
      // 传递成员的用户ID
      onDelete(member.user_id || member.id);
    }
    setDeleteModalVisible(false);
  };

  // 取消删除
  const handleCancelDelete = () => {
    setDeleteModalVisible(false);
  };

  // 处理撤回邀请按钮点击
  const handleRevokeClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setRevokeModalVisible(true);
  };

  // 确认撤回邀请
  const handleConfirmRevoke = () => {
    if (onRevokeInvitation && member.invitation_id) {
      onRevokeInvitation(member.invitation_id);
    }
    setRevokeModalVisible(false);
  };

  // 取消撤回邀请
  const handleCancelRevoke = () => {
    setRevokeModalVisible(false);
  };

  return (
    <>
      <div 
        className={`relative bg-white rounded-xl overflow-hidden shadow-sm transition-all duration-200 cursor-pointer hover:scale-[1.02] active:scale-[0.98] ${
          member.member_status === 'invited' 
            ? 'border-2 border-orange-300 border-dashed hover:shadow-lg hover:border-orange-400' 
            : 'border border-gray-200 hover:shadow-md'
        }`}
        onClick={() => navigate(`/personal-info/${member.user_id}`)}
      >
        {/* 背景头像 */}
        <div className="absolute inset-0 opacity-10">
          <img
            src={member.avatar || ''}
            alt={member.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>

        {/* 内容区域 */}
        <div className="relative z-10 p-4">
          {/* 身份标签 */}
          <div className="flex justify-end mb-3">
            <Tag
              className={`${getCareerInfo().color} border-0 text-xs font-medium px-2 py-1 rounded-full`}
            >
              {getCareerInfo().label}
            </Tag>
          </div>

          {/* 成员信息 */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* 头像 */}
              <UserAvatar
                size={48}
                src={member.avatar}
                user={member}
                className="border-2 border-white shadow-sm"
              />

              {/* 名称 */}
              <div className="flex-1">
                <div className="font-medium text-gray-900 text-base">
                  {member.name || '未知成员'}
                </div>
              </div>
            </div>

            {/* 获赞数 */}
            <div className="flex items-center gap-1 text-gray-600">
              <LikeOutlined className="text-sm" />
              <span className="text-sm font-medium">
                {formatLikeCount(member.like_count || 0)}
              </span>
            </div>
          </div>
        </div>

        {/* 操作按钮 - 仅队长可见 */}
        {showDelete && (
          <>
            {/* 正式成员：显示删除按钮（不能删除队长） */}
            {member.member_status !== 'invited' && member.role !== 'captain' && (
              <button
                onClick={handleDeleteClick}
                className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600 transition-colors duration-200 z-20 border-2 border-white shadow-sm"
                title="移除成员"
              >
                <CloseOutlined />
              </button>
            )}
            
            {/* 邀请成员：显示撤回邀请按钮 */}
            {member.member_status === 'invited' && (
              <button
                onClick={handleRevokeClick}
                className="absolute top-1 right-1 w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-orange-600 transition-colors duration-200 z-20 border-2 border-white shadow-sm"
                title="撤回邀请"
              >
                <UndoOutlined />
              </button>
            )}
          </>
        )}
      </div>

      {/* 删除确认 Modal */}
      <Modal
        title="确认移除成员"
        open={deleteModalVisible}
        onOk={handleConfirmDelete}
        onCancel={handleCancelDelete}
        okText="确认移除"
        cancelText="取消"
        okButtonProps={{ danger: true }}
      >
        <p>确定要移除成员 <strong>{member.name}</strong> 吗？</p>
        <p className="text-gray-500 text-sm mt-2">此操作不可撤销。</p>
      </Modal>

      {/* 撤回邀请确认 Modal */}
      <Modal
        title="确认撤回邀请"
        open={revokeModalVisible}
        onOk={handleConfirmRevoke}
        onCancel={handleCancelRevoke}
        okText="确认撤回"
        cancelText="取消"
        okButtonProps={{ danger: true }}
      >
        <p>确定要撤回对 <strong>{member.name}</strong> 的邀请吗？</p>
        <p className="text-gray-500 text-sm mt-2">撤回后该用户将无法通过此邀请加入团队。</p>
      </Modal>
    </>
  );
};

export default MemberCard; 